import React, { useContext, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { SSEContext } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { playAlertSound } from "@/lib/alertSound";
import { api } from "@/lib/api";

/**
 * NotificationsHub — invisible component. Mounted once inside AppShell to
 * convert SSE lifecycle + news events into toasts + sound, gated by the
 * user's notification prefs. Pure side-effect component.
 */
export default function NotificationsHub() {
  const { user } = useAuth();
  const { lastLifecycle, lastNewsAlert } = useContext(SSEContext);
  const { toast } = useToast();
  const lastIds = useRef({ lc: null, news: null });

  // Pull prefs (refetch every 30s — settings page may flip toggles)
  const { data: prefsData } = useQuery({
    queryKey: ["prefs"],
    queryFn: () => api.get("/prefs").then((r) => r.data),
    enabled: !!user,
    refetchInterval: 30_000,
  });
  const prefs = prefsData?.prefs;

  // Lifecycle → toast + sound
  useEffect(() => {
    if (!user || !prefs || !lastLifecycle?.id) return;
    if (lastIds.current.lc === lastLifecycle.id) return;
    lastIds.current.lc = lastLifecycle.id;
    if (!prefs.in_app) return;
    if (!prefs.events?.includes(lastLifecycle.event_type)) return;
    if (prefs.symbols?.length && lastLifecycle.symbol && !prefs.symbols.includes(lastLifecycle.symbol)) return;

    const titles = {
      CREATED:  `📋 New ${(lastLifecycle.mode || "").toUpperCase()} setup · ${lastLifecycle.symbol}`,
      ENTERED:  `🎯 ${lastLifecycle.symbol} entered the zone`,
      TP1_HIT:  `🎯 ${lastLifecycle.symbol} hit Target 1`,
      TP2_HIT:  `🎯🎯 ${lastLifecycle.symbol} hit Target 2`,
      SL_HIT:   `🛑 ${lastLifecycle.symbol} stopped out`,
      EXPIRED:  `⏱ ${lastLifecycle.symbol} setup expired`,
    };
    toast({
      title: titles[lastLifecycle.event_type] || `${lastLifecycle.symbol} · ${lastLifecycle.event_type}`,
      description: lastLifecycle.message,
      variant: lastLifecycle.event_type === "SL_HIT" ? "destructive" : "default",
    });
    if (prefs.sound) playAlertSound();
  }, [lastLifecycle, prefs, toast, user]);

  // News HIGH RISK → toast + sound
  useEffect(() => {
    if (!user || !prefs || !lastNewsAlert?.event_time) return;
    const key = lastNewsAlert.event_time + ":" + lastNewsAlert.currency;
    if (lastIds.current.news === key) return;
    lastIds.current.news = key;
    if (!prefs.in_app) return;
    if (!prefs.events?.includes("NEWS_HIGH_RISK")) return;
    toast({
      title: lastNewsAlert.title || "High-risk news",
      description: lastNewsAlert.body || "",
      variant: "destructive",
    });
    if (prefs.sound) playAlertSound();
  }, [lastNewsAlert, prefs, toast, user]);

  return null;
}
